/**
 * SignalingClient — single persistent WebSocket wrapper.
 *
 * Manages:
 * - Connection lifecycle (connect, reconnect, close)
 * - Message sending with ack/retry
 * - Heartbeat/pong
 * - Event handlers for meeting lifecycle events
 */
class SignalingClient {
  constructor({ url, onOpen, onMessage, onClose, onError }) {
    this.url = url;
    this.onOpen = onOpen || (() => {});
    this.onMessage = onMessage || (() => {});
    this.onClose = onClose || (() => {});
    this.onError = onError || (() => {});

    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.shouldReconnect = true;

    this._heartbeatInterval = null;
    this._pendingAcks = new Map();
    this._messageCounter = 0;
  }

  connect() {
    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log("[SignalingClient] Connected");
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this._startHeartbeat();
        this.onOpen();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this._handleMessage(message);
        } catch (err) {
          console.error("[SignalingClient] Error parsing message:", err);
        }
      };

      this.ws.onclose = (event) => {
        console.log(
          `[SignalingClient] Disconnected: code=${event.code} reason=${event.reason}`
        );
        this.isConnected = false;
        this._stopHeartbeat();
        this._rejectAllPending();
        this.onClose(event);

        if (
          this.shouldReconnect &&
          this.reconnectAttempts < this.maxReconnectAttempts
        ) {
          this._scheduleReconnect();
        }
      };

      this.ws.onerror = (err) => {
        console.error("[SignalingClient] Error:", err);
        this.onError(err);
      };
    } catch (err) {
      console.error("[SignalingClient] Connection error:", err);
      if (this.shouldReconnect) {
        this._scheduleReconnect();
      }
    }
  }

  send(type, payload, options = {}) {
    const message = {
      type,
      payload: payload || {},
      messageId: options.messageId || this._generateMessageId(),
      timestamp: new Date().toISOString(),
    };

    const data = JSON.stringify(message);

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      console.warn("[SignalingClient] Cannot send, WebSocket not open");
    }

    return message.messageId;
  }

  sendWithAck(type, payload, timeout = 10000) {
    return new Promise((resolve, reject) => {
      const messageId = this._generateMessageId();
      const message = {
        type,
        payload: payload || {},
        messageId,
        timestamp: new Date().toISOString(),
      };

      this._pendingAcks.set(messageId, {
        resolve,
        reject,
        attempts: 0,
        timeout,
        type,
      });

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(message));
      } else {
        reject(new Error("WebSocket not connected"));
        this._pendingAcks.delete(messageId);
        return;
      }

      // Set timeout
      setTimeout(() => {
        const entry = this._pendingAcks.get(messageId);
        if (entry) {
          if (entry.attempts < 3) {
            entry.attempts++;
            // Resend
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
              this.ws.send(JSON.stringify(message));
            }
            // Extend timeout
            setTimeout(() => {
              const e = this._pendingAcks.get(messageId);
              if (e) {
                e.reject(new Error(`Ack timeout for ${type}`));
                this._pendingAcks.delete(messageId);
              }
            }, timeout * 1.5);
          } else {
            reject(new Error(`Max retries reached for ${type}`));
            this._pendingAcks.delete(messageId);
          }
        }
      }, timeout);
    });
  }

  close() {
    console.log("[SignalingClient] Closing connection");
    this.shouldReconnect = false;
    this._stopHeartbeat();
    this._rejectAllPending();

    if (this.ws) {
      this.ws.close(1000, "Client closing");
      this.ws = null;
    }
  }

  // --- Private methods ---

  _handleMessage(message) {
    const { type, messageId } = message;

    // Check if this is an ack for a pending message
    if (type === "join-ack" || type === "reconnect-ack") {
      const pending = this._pendingAcks.get(messageId);
      if (pending) {
        pending.resolve(message);
        this._pendingAcks.delete(messageId);
      }
    }

    // Pass to callback
    if (this.onMessage) {
      this.onMessage(message);
    }
  }

  _startHeartbeat() {
    this._heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send("heartbeat", {});
      }
    }, 15000);
  }

  _stopHeartbeat() {
    if (this._heartbeatInterval) {
      clearInterval(this._heartbeatInterval);
      this._heartbeatInterval = null;
    }
  }

  _scheduleReconnect() {
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      16000
    );
    this.reconnectAttempts++;

    console.log(
      `[SignalingClient] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`
    );

    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect();
      }
    }, delay);
  }

  _rejectAllPending() {
    for (const entry of this._pendingAcks.values()) {
      entry.reject(new Error("Connection closed"));
    }
    this._pendingAcks.clear();
  }

  _generateMessageId() {
    this._messageCounter++;
    return `msg_${Date.now()}_${this._messageCounter}`;
  }
}

export default SignalingClient;
